import React from "react"
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from "react-router-dom"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import { CssBaseline, AppBar, Toolbar, Typography, Box } from "@mui/material"
import { AuthProvider } from "./contexts/AuthContext"
import ProtectedRoute from "./components/ProtectedRoute"
import AdminLayout from "./components/AdminLayout"
import HomePage from "./pages/HomePage"
import PrivacyPage from "./pages/PrivacyPage"
import TermsPage from "./pages/TermsPage"
import SupportPage from "./pages/SupportPage"
import BathroomDetailsPage from "./pages/BathroomDetailsPage"
import BathroomDeepLink from "./pages/BathroomDeepLink"
import AdminLogin from "./pages/AdminLogin"
import AdminDashboard from "./pages/AdminDashboard"
import AdminUsers from "./pages/AdminUsers"
import AdminBathrooms from "./pages/AdminBathrooms"
import AdminActivity from "./pages/AdminActivity"

// Create a theme with purple gradient colors similar to the mobile app
const theme = createTheme({
  palette: {
    primary: {
      main: "#8E2DE2",
      light: "#B794F6",
      dark: "#4A00E0",
    },
    secondary: {
      main: "#4A00E0",
    },
    background: {
      default: "#f5f5f5",
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
    h3: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "linear-gradient(45deg, #4A00E0 30%, #8E2DE2 90%)",
        },
      },
    },
  },
})

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <Box sx={{ flexGrow: 1 }}>
                  <AppBar position="static" elevation={0}>
                    <Toolbar>
                      <Link
                        to="/"
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                          flexGrow: 1,
                        }}
                      >
                        <Typography variant="h6" component="div" sx={{ cursor: "pointer" }}>
                          🚽 TNKL
                        </Typography>
                      </Link>
                    </Toolbar>
                  </AppBar>
                  <HomePage />
                  <Box
                    component="footer"
                    sx={{
                      py: 3,
                      px: 2,
                      mt: "auto",
                      backgroundColor: "background.paper",
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mb: 1 }}>
                      <Link
                        to="/privacy"
                        style={{
                          color: "#8E2DE2",
                          textDecoration: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        Privacy Policy
                      </Link>
                      <Link
                        to="/terms"
                        style={{
                          color: "#8E2DE2",
                          textDecoration: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        Terms of Service
                      </Link>
                      <Link
                        to="/support"
                        style={{
                          color: "#8E2DE2",
                          textDecoration: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        Support
                      </Link>
                    </Box>
                    <Typography variant="body2" color="text.secondary" align="center">
                      © 2026 TNKL. All rights reserved.
                    </Typography>
                  </Box>
                </Box>
              }
            />
            <Route path="/bathroom/:id/*" element={<BathroomDetailsPage />} />
            <Route path="/bathroom/:locationId/:bathroomId" element={<BathroomDeepLink />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/support" element={<SupportPage />} />

            {/* Admin Routes - Discrete and Protected */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminUsers />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bathrooms"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminBathrooms />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/activity"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminActivity />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<div>Page Not Found</div>} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
